import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Microphone, Stop, PaperPlaneRight, CheckCircle, GraduationCap, Question, Lightbulb } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const NOTE_TYPES = ["scheduling", "call_off", "visit_note", "client_concern", "caregiver_issue", "supply_need", "message", "urgent", "other"];
const NOTE_TAGS = ["follow-up", "complaint", "incident", "compliment", "schedule", "family", "billing", "supplies", "safety", "availability", "training", "health-change"];
const URGENCIES = ["routine", "today", "urgent"];

const TYPE_LABELS = { scheduling: "Scheduling", call_off: "Call-off", visit_note: "Visit note", client_concern: "Client concern", caregiver_issue: "Caregiver issue", supply_need: "Supply need", message: "Message", urgent: "URGENT", other: "Other" };

function FileAsSelect({ n, onDone }) {
  const [apptOpen, setApptOpen] = useState(false);
  const [apptDate, setApptDate] = useState("");
  const file = async (kind, extra = {}) => {
    try {
      await api.put(`/notes/quick/${n.id}/categorize`, { kind, ...extra });
      toast.success(kind === "complaint" ? "Filed to the complaints log — it's on the dashboard for review"
        : kind === "incident" ? "Filed to the incident log"
        : kind === "appointment" ? "On the calendar as a follow-up" : "Note type updated");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <span className="inline-flex items-center gap-1" data-testid={`quick-note-fileas-wrap-${n.id}`}>
      <select value="" onChange={(e) => {
        const v = e.target.value;
        if (!v) return;
        if (v === "appointment") { setApptOpen(true); return; }
        if (v === "complaint" || v === "incident") { file(v); return; }
        file("note_type", { note_type: v });
      }} className="h-6 rounded-md border border-stone-200 bg-white px-1 text-[10px] text-stone-500" data-testid={`quick-note-fileas-${n.id}`}>
        <option value="">File as…</option>
        <option value="complaint">Complaint → complaints log</option>
        <option value="incident">Incident → incident log</option>
        <option value="appointment">Appointment → calendar…</option>
        <optgroup label="Change note type">
          {Object.keys(TYPE_LABELS).filter((t) => t !== n.note_type).map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </optgroup>
      </select>
      {apptOpen && (
        <span className="inline-flex items-center gap-1">
          <input type="date" value={apptDate} onChange={(e) => setApptDate(e.target.value)}
            className="h-6 rounded-md border border-stone-200 px-1 text-[10px]" data-testid={`quick-note-appt-date-${n.id}`} />
          <button type="button" onClick={async () => { await file("appointment", { date: apptDate }); setApptOpen(false); }}
            disabled={!apptDate} className="text-[10px] font-semibold text-primary hover:underline disabled:opacity-40"
            data-testid={`quick-note-appt-save-${n.id}`}>Add</button>
        </span>
      )}
      {n.categorized_as && n.categorized_as !== "appointment" && (
        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary" data-testid={`quick-note-filed-${n.id}`}>→ {n.categorized_as} log</span>
      )}
    </span>
  );
}
const URGENCY_CLS = { routine: "bg-stone-100 text-stone-500", today: "bg-[#B7791F]/10 text-[#8a5d14]", urgent: "bg-[#C0392B]/10 text-[#C0392B]" };

export default function QuickNoteCard({ presetClient = null, presetEmployee = null, presetType = "", compact = false }) {
  const { user } = useAuth();
  const isStaff = user?.role !== "admin";
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [notes, setNotes] = useState([]);
  const [coachOn, setCoachOn] = useState(true);
  const [coach, setCoach] = useState(null);
  const [coachBusy, setCoachBusy] = useState(false);
  const [procs, setProcs] = useState({});
  const [rewriteKept, setRewriteKept] = useState(false);
  const [helpQ, setHelpQ] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpLog, setHelpLog] = useState([]);
  const [complaintPrompt, setComplaintPrompt] = useState(null);
  const [helpSession, setHelpSession] = useState("");
  const [helpBusy, setHelpBusy] = useState(false);
  const [fixGrammar, setFixGrammar] = useState(true);
  const [rewriteAccepted, setRewriteAccepted] = useState(false);
  const [relinkFor, setRelinkFor] = useState(null);
  const [relinkClient, setRelinkClient] = useState("");
  const [relinkEmp, setRelinkEmp] = useState("");
  const [relinkClients, setRelinkClients] = useState([""]);
  const [relinkEmps, setRelinkEmps] = useState([""]);
  const [addToCalendar, setAddToCalendar] = useState(true);
  const [actedPatterns, setActedPatterns] = useState(new Set());
  // compact mode (dashboard sidebar): note lists start collapsed so the card stays short
  const [showAttention, setShowAttention] = useState(!compact);
  const [showRecent, setShowRecent] = useState(!compact);
  const recorder = useRef(null);
  const chunks = useRef([]);

  const loadNotes = () => api.get(`/notes/quick${presetClient ? `?client_id=${presetClient.id}` : ""}`).then((r) => setNotes(r.data.notes)).catch(() => {});
  useEffect(() => {
    loadNotes();
    api.get("/notes/quick/coach-pref").then((r) => setCoachOn(r.data.on !== false)).catch(() => {});
    api.get("/clients").then((r) => {
      let list = r.data.clients || r.data || [];
      if (isStaff) {
        const mine = new Set(user?.assigned_clients || []);
        list = list.filter((c) => mine.has(c.id));
      }
      setClients(list);
    }).catch(() => {});
    api.get("/users").then((r) => setStaff((r.data || []).filter((u) => u.role === "staff"))).catch(() => {});
  }, []);

  const toggleRecord = async () => {
    if (recording) {
      recorder.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunks.current = [];
      rec.ondataavailable = (e) => chunks.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setTranscribing(true);
        try {
          const blob = new Blob(chunks.current, { type: rec.mimeType || "audio/webm" });
          const fd = new FormData();
          fd.append("file", new File([blob], "note.webm", { type: blob.type }));
          const { data } = await api.post("/transcribe", fd, { timeout: 120000 });
          setText((t) => (t ? t + " " : "") + (data.text || "").trim());
          toast.success("Transcribed — check it, then classify");
        } catch (e) { toast.error(formatApiError(e)); }
        setTranscribing(false);
      };
      recorder.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Microphone not available — check browser permission");
    }
  };

  const classify = async () => {
    if (!text.trim()) { toast.error("Type or dictate the note first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/notes/quick/classify", { text, fix_grammar: fixGrammar, preset_client_id: presetClient?.id || "", preset_employee_id: presetEmployee?.id || "" });
      const c = data.classification;
      if (presetClient) { c.client_id = presetClient.id; c.client_name = presetClient.name; }
      if (presetEmployee) { c.employee_id = presetEmployee.id; c.employee_name = presetEmployee.name; }
      if (presetType) c.note_type = presetType;
      setProposal(c);
      setCoach(null);
      setRewriteKept(false);
      setRewriteAccepted(false);
      setHelpLog([]);
      setHelpSession("");
      setHelpOpen(false);
      setProcs({});
      if (coachOn && !isStaff) {
        setCoachBusy(true);
        try {
          const r = await api.post("/notes/quick/coach", { text, note_type: c.note_type, client_id: c.client_id || "", employee_id: c.employee_id || "" });
          const co = r.data.coach;
          setCoach(co);
          const sel = {};
          (co.procedures || []).forEach((p) => { sel[p.key] = true; });
          setProcs(sel);
          if (sel.follow_up && !c.needs_follow_up) setP2(c, "needs_follow_up", true);
        } catch { /* coaching is advisory — classification still stands */ }
        setCoachBusy(false);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const setP2 = (base, k, v) => setProposal((p) => ({ ...(p || base), [k]: v }));

  const toggleCoach = async () => {
    const next = !coachOn;
    setCoachOn(next);
    if (!next) setCoach(null);
    try { await api.put("/notes/quick/coach-pref", { on: next }); } catch { /* keep local state */ }
  };

  const askHelp = async () => {
    if (!helpQ.trim()) { toast.error("Type your question first"); return; }
    setHelpBusy(true);
    const q = helpQ.trim();
    try {
      const { data } = await api.post("/notes/quick/help", { question: q, note_text: text, session_id: helpSession || undefined }, { timeout: 90000 });
      if (data.session_id) setHelpSession(data.session_id);
      setHelpLog((l) => [...l, { q, a: data.answer, sources: data.sources || [] }]);
      setHelpQ("");
    } catch (e) { toast.error(formatApiError(e)); }
    setHelpBusy(false);
  };

  const reclean = async (n) => {
    try {
      await api.post(`/notes/quick/${n.id}/reclean`, {});
      toast.success("Spelling & grammar cleaned — the raw original is kept");
      loadNotes();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const relink = async (nid) => {
    try {
      await api.post(`/notes/quick/${nid}/relink`, {
        client_ids: relinkClients.filter(Boolean), employee_ids: relinkEmps.filter(Boolean),
        client_id: relinkClient, employee_id: relinkEmp });
      toast.success("Matched — note filed to their records (stays here until you clear it)");
      setRelinkFor(null);
      setRelinkClient("");
      setRelinkEmp("");
      setRelinkClients([""]);
      setRelinkEmps([""]);
      loadNotes();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setTags = async (n, tags) => {
    try {
      await api.post(`/notes/quick/${n.id}/tags`, { tags });
      loadNotes();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const patternAction = async (p) => {
    try {
      await api.post("/notes/quick/pattern-followup", {
        follow_up: p.action.follow_up, days: p.action.days,
        client_id: p.person_kind === "client" ? p.person_id : "",
        employee_id: p.person_kind === "employee" ? p.person_id : "",
        client_name: p.person_kind === "client" ? p.person : "",
        employee_name: p.person_kind === "employee" ? p.person : "",
        pattern_key: p.key});
      setActedPatterns((s) => new Set([...s, p.key + p.person_id]));
      toast.success("Follow-up created — it's on the dashboard and calendar");
      loadNotes();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/notes/quick/save", { ...proposal, original: text, procedures: procs, add_to_calendar: addToCalendar,
        coach_meta: coach ? { shown: true, rewrite_suggested: coach.rewrite || "", rewrite_accepted: rewriteAccepted, procedures_suggested: (coach.procedures || []).map((p) => p.key), patterns_shown: (coach.patterns || []).map((p) => p.key) } : { shown: false } });
      const filed = [];
      if (data.note.visit_documented_for) filed.push(`visit log for ${data.note.visit_documented_for}`);
      else if (data.note.filed_to_client) filed.push("the client's record");
      if (data.note.filed_to_employee) filed.push("the employee's record");
      (data.note.procedures_filed || []).forEach((p) => filed.push(p));
      toast.success(filed.length ? `Note filed — also posted to ${filed.join(" and ")}` : "Note filed");
      if (data.possible_complaint) setComplaintPrompt({ id: data.note_id, text: (data.note.cleaned || "").slice(0, 160) });
      setText("");
      setProposal(null);
      setCoach(null);
      setProcs({});
      setHelpLog([]);
      setHelpSession("");
      setHelpOpen(false);
      setHelpQ("");
      setRewriteAccepted(false);
      setAddToCalendar(true);
      setActedPatterns(new Set());
      loadNotes();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const markDone = async (n) => {
    try {
      await api.post(`/notes/quick/${n.id}/done`, {});
      loadNotes();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setP = (k, v) => setProposal((p) => ({ ...p, [k]: v }));

  const attentionNotes = notes.filter((n) => !n.done && (n.needs_follow_up || n.needs_match || n.matched_at));
  const recentNotes = notes.filter((n) => !attentionNotes.some((a) => a.id === n.id));
  const jumpTo = (id) => document.querySelector(`[data-testid="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="mb-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="quick-note-card">
      <div className="flex items-start justify-between gap-3">
        <p className="font-heading text-lg font-semibold text-stone-900">
          {presetType === "visit_note" ? "Report the visit" : "Quick note"}
          {presetClient ? <span className="text-stone-400"> — {presetClient.name}</span> : presetEmployee ? <span className="text-stone-400"> — {presetEmployee.name}</span> : null}
        </p>
        {!isStaff && (
          <button type="button" onClick={toggleCoach}
            className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${coachOn ? "border-primary/40 bg-primary/5 text-primary" : "border-stone-300 text-stone-400 hover:text-stone-600"}`}
            title={coachOn ? "Coaching is on — the system suggests rewrites, explains why, and flags required procedures. Tap for quick mode." : "Quick mode — no coaching. Tap to turn coaching back on."}
            data-testid="quick-note-coach-toggle">
            <GraduationCap size={14} /> Coach {coachOn ? "on" : "off"}
          </button>
        )}
      </div>
      <p className="mt-0.5 text-xs text-stone-500">
        {presetType === "visit_note"
          ? "Say it or type it however it comes out — what was done, how they were, anything to watch. It files itself onto the visit log and the right records."
          : "Say it or type it however it comes out — spelling doesn't matter. The system classifies it, cleans it up, and you confirm before it's filed."}
      </p>

      <div className="mt-2 flex flex-wrap gap-1.5" data-testid="quick-note-jump-row">
        {attentionNotes.length > 0 && (
          <button type="button" onClick={() => { setShowAttention((v) => !v); if (!showAttention) setTimeout(() => jumpTo("quick-note-attention"), 50); }}
            className="rounded-full border border-[#B7791F]/40 bg-[#B7791F]/10 px-3 py-1 text-[11px] font-semibold text-[#8a5d14]"
            data-testid="quick-note-jump-attention">{showAttention ? "↑ Hide open items" : `↓ Open items (${attentionNotes.length})`}</button>
        )}
        {recentNotes.length > 0 && (
          <button type="button" onClick={() => { setShowRecent((v) => !v); if (!showRecent) setTimeout(() => jumpTo("quick-note-recent"), 50); }}
            className="rounded-full border border-stone-200 px-3 py-1 text-[11px] font-medium text-stone-500 hover:border-primary/40 hover:text-primary"
            data-testid="quick-note-jump-recent">{showRecent ? "↑ Hide last few notes" : `↓ Last few notes (${recentNotes.length})`}</button>
        )}
      </div>

      <div data-testid="quick-note-work">
      <div className="mt-4 flex items-start gap-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={compact ? 2 : 3}
          placeholder={presetType === "visit_note"
            ? "e.g. \"done with mrs jenkins, made her lunch she ate about half, ankle looked a little puffy\""
            : 'e.g. "martha jenkins dauther called shes not happy w tuesdays aid being late agian"'}
          className="flex-1 rounded-lg border border-stone-300 px-3 py-2 text-sm" data-testid="quick-note-text" />
        <div className="flex flex-col gap-2">
          <button onClick={toggleRecord} disabled={transcribing}
            className={`flex h-11 w-11 items-center justify-center rounded-full transition-colors ${recording ? "animate-pulse bg-[#C0392B] text-white" : "bg-primary text-primary-foreground hover:bg-stone-900"} ${transcribing ? "opacity-50" : ""}`}
            title={recording ? "Stop recording" : "Dictate the note"} data-testid="quick-note-record">
            {recording ? <Stop size={18} weight="fill" /> : <Microphone size={18} />}
          </button>
          <Button onClick={classify} disabled={busy || transcribing} className="rounded-full bg-stone-900 text-xs text-white hover:bg-primary" data-testid="quick-note-classify">
            {transcribing ? "Listening…" : busy && !proposal ? "Reading…" : "Classify"}
          </Button>
        </div>
      </div>
      {recording && <p className="mt-1 text-xs font-semibold text-[#C0392B]" data-testid="quick-note-recording">Recording… tap the red button when done.</p>}
      {transcribing && <p className="mt-1 text-xs text-stone-400" data-testid="quick-note-transcribing">Turning your voice into text…</p>}
      <label className="mt-2 flex w-fit cursor-pointer items-center gap-2 text-xs text-stone-500">
        <input type="checkbox" checked={fixGrammar} onChange={(e) => setFixGrammar(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="quick-note-fix-grammar" />
        Fix spelling &amp; grammar <span className="text-stone-400">(your raw words are always saved too)</span>
      </label>

      {proposal && (
        <div className="mt-4 rounded-lg border border-primary/25 bg-primary/5 p-4" data-testid="quick-note-proposal">
          <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Confirm before it's filed — fix anything it got wrong</p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="block">
              <span className="text-[11px] font-semibold text-stone-400">Type</span>
              <select value={proposal.note_type} onChange={(e) => setP("note_type", e.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="quick-note-type">
                {NOTE_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-stone-400">Client</span>
              <select value={proposal.client_id} onChange={(e) => { const c = clients.find((x) => x.id === e.target.value); setP("client_id", e.target.value); setP("client_name", (c && (c.full_name || c.name)) || proposal.client_name); }} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="quick-note-client">
                <option value="">{proposal.client_name ? `${proposal.client_name} (not matched)` : "— none —"}</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name || c.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-stone-400">Employee</span>
              <select value={proposal.employee_id} onChange={(e) => { const s = staff.find((x) => x.id === e.target.value); setP("employee_id", e.target.value); setP("employee_name", s?.name || proposal.employee_name); }} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="quick-note-employee">
                <option value="">{proposal.employee_name ? `${proposal.employee_name} (not matched)` : "— none —"}</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-stone-400">Date it happened</span>
              <input type="date" value={proposal.event_date} onChange={(e) => setP("event_date", e.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="quick-note-date" />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-stone-400">Urgency</span>
              <select value={proposal.urgency} onChange={(e) => setP("urgency", e.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="quick-note-urgency">
                {URGENCIES.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
            <label className="flex items-end gap-2 pb-1">
              <input type="checkbox" checked={proposal.needs_follow_up} onChange={(e) => setP("needs_follow_up", e.target.checked)} className="h-4 w-4 accent-primary" data-testid="quick-note-followup-check" />
              <span className="text-xs font-medium text-stone-600">Needs follow-up</span>
            </label>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="quick-note-reporter">
            <label className="block">
              <span className="text-[11px] font-semibold text-stone-400">Who called / reported it</span>
              <input value={proposal.reported_by_name || ""} onChange={(e) => setP("reported_by_name", e.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="quick-note-reporter-name" placeholder="Caller's name" />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-stone-400">They are a…</span>
              <select value={proposal.reported_by_role || "other"} onChange={(e) => setP("reported_by_role", e.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="quick-note-reporter-role">
                {[["client", "Client"], ["family", "Family member"], ["employee", "Employee"], ["office", "Office"], ["other", "Other"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-stone-400">Their phone (saved as a contact)</span>
              <input value={proposal.reported_by_phone || ""} onChange={(e) => setP("reported_by_phone", e.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="quick-note-reporter-phone" placeholder="570-555-…" />
            </label>
          </div>
          <label className="mt-3 block">
            <span className="text-[11px] font-semibold text-stone-400">The note that gets filed — edit freely</span>
            <textarea value={proposal.cleaned} onChange={(e) => setP("cleaned", e.target.value)} rows={2} className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="quick-note-cleaned" />
          </label>
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="quick-note-tags-editor">
            {(proposal.tags || []).map((t) => (
              <button key={t} type="button" title="Remove tag"
                onClick={() => setP("tags", (proposal.tags || []).filter((x) => x !== t))}
                className="rounded-full border border-primary bg-primary px-2.5 py-1 text-[11px] font-medium text-white"
                data-testid={`quick-note-tag-${t}`}>#{t} ×</button>
            ))}
            <select value="" onChange={(e) => { if (e.target.value) setP("tags", [...(proposal.tags || []), e.target.value]); }}
              className="h-7 rounded-md border border-stone-200 bg-white px-1.5 text-[11px] text-stone-500" data-testid="quick-note-tag-add">
              <option value="">+ tag</option>
              {NOTE_TAGS.filter((t) => !(proposal.tags || []).includes(t)).map((t) => <option key={t} value={t}>#{t}</option>)}
            </select>
          </div>
          {proposal.follow_up && (
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="block sm:col-span-2">
                <span className="text-[11px] font-semibold text-stone-400">Suggested follow-up</span>
                <input value={proposal.follow_up} onChange={(e) => setP("follow_up", e.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="quick-note-followup" />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold text-stone-400">Follow up by</span>
                <input type="date" value={proposal.follow_up_date || ""} onChange={(e) => setP("follow_up_date", e.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="quick-note-followup-date" />
              </label>
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <Button onClick={save} disabled={busy} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="quick-note-save">
              <PaperPlaneRight size={14} className="mr-1.5" /> {busy ? "Filing…" : "File the note"}
            </Button>
            <Button variant="outline" onClick={() => { setProposal(null); setCoach(null); setProcs({}); setHelpLog([]); setHelpSession(""); setHelpOpen(false); setRewriteAccepted(false); setAddToCalendar(true); setActedPatterns(new Set()); }} className="rounded-full border-stone-300" data-testid="quick-note-discard">Discard</Button>
          </div>
          {(proposal.event_date || (proposal.needs_follow_up && proposal.follow_up_date)) && (
            <label className="mt-3 flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs text-stone-600">
              <input type="checkbox" checked={addToCalendar} onChange={(e) => setAddToCalendar(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="quick-note-add-calendar" />
              <span>Put this on the calendar{proposal.follow_up_date ? ` — ${proposal.follow_up_date}` : proposal.event_date ? ` — ${proposal.event_date}` : ""} <span className="text-stone-400">(uncheck to keep it off)</span></span>
            </label>
          )}

          {coachBusy && (
            <p className="mt-3 text-xs text-stone-400" data-testid="quick-note-coach-loading">Coach is reading the note against your policies & values…</p>
          )}
          {coach && !isStaff && (
            <div className="mt-4 rounded-lg border border-[#6A3E97]/25 bg-[#6A3E97]/5 p-4" data-testid="quick-note-coach-panel">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#4c2a75]">
                <GraduationCap size={14} /> Coach — grounded in your policies, values & operating principles
              </p>

              {coach.rewrite && coach.rewrite !== proposal.cleaned && !rewriteKept && (
                <div className="mt-3 rounded-lg border border-stone-200 bg-white p-3" data-testid="quick-note-coach-rewrite">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Suggested wording</p>
                  <p className="mt-1 text-sm leading-relaxed text-stone-800">{coach.rewrite}</p>
                  <div className="mt-2 flex gap-2">
                    <button type="button" onClick={() => { setP("cleaned", coach.rewrite); setRewriteAccepted(true); }}
                      className="rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-white hover:bg-stone-900" data-testid="quick-note-use-rewrite">
                      Use this version
                    </button>
                    <button type="button" onClick={() => setRewriteKept(true)}
                      className="rounded-full border border-stone-300 px-3 py-1 text-[11px] font-medium text-stone-500 hover:bg-stone-100" data-testid="quick-note-keep-mine">
                      Keep my words
                    </button>
                  </div>
                </div>
              )}

              {(coach.education || []).length > 0 && (
                <div className="mt-3 rounded-lg border border-stone-200 bg-white p-3" data-testid="quick-note-coach-education">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Keep in mind for this one</p>
                  <ul className="mt-1.5 space-y-1.5">
                    {coach.education.map((e, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs leading-relaxed text-stone-700" data-testid={`quick-note-coach-edu-${i}`}>
                        <Lightbulb size={13} className="mt-0.5 shrink-0 text-[#B7791F]" />
                        <span>{e.point}{e.why ? <span className="text-stone-400"> — {e.why}</span> : null}{e.source ? <span className="text-stone-400"> ({e.source})</span> : null}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(coach.patterns || []).length > 0 && (
                <div className="mt-3 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-3" data-testid="quick-note-coach-patterns">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-[#8a5d14]">Pattern alert — spotted in recent notes</p>
                  <div className="mt-2 space-y-2">
                    {coach.patterns.map((p, i) => (
                      <div key={i} className="rounded-lg border border-stone-200 bg-white p-3" data-testid={`quick-note-pattern-${i}`}>
                        <p className="text-xs leading-relaxed text-stone-700">{p.text}</p>
                        {p.action && (actedPatterns.has(p.key + p.person_id) ? (
                          <p className="mt-1.5 text-[11px] font-semibold text-[#1e8449]" data-testid={`quick-note-pattern-done-${i}`}>Follow-up created — on the dashboard &amp; calendar</p>
                        ) : (
                          <button type="button" onClick={() => patternAction(p)}
                            className="mt-2 rounded-full bg-[#B7791F] px-3 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-stone-900"
                            data-testid={`quick-note-pattern-action-${i}`}>
                            {p.action.label} → follow-up in {p.action.days} days
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(coach.procedures || []).length > 0 && (
                <div className="mt-3 rounded-lg border border-[#C0392B]/25 bg-[#C0392B]/5 p-3" data-testid="quick-note-coach-procedures">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-[#C0392B]">Policy says this needs to be logged — uncheck anything you disagree with</p>
                  <div className="mt-2 space-y-1.5">
                    {coach.procedures.filter((p) => p.key !== "follow_up").map((p) => (
                      <label key={p.key} className="flex cursor-pointer items-start gap-2 text-xs" data-testid={`quick-note-proc-${p.key}`}>
                        <input type="checkbox" checked={!!procs[p.key]} onChange={(e) => setProcs((s) => ({ ...s, [p.key]: e.target.checked }))} className="mt-0.5 h-4 w-4 accent-[#C0392B]" />
                        <span className="text-stone-700">
                          <span className="font-semibold">{p.key === "incident" ? "Log an incident report" : p.key === "complaint" ? "Log a complaint" : "Log a call-out"}</span>
                          {p.reason && <span className="text-stone-500"> — {p.reason}</span>}
                        </span>
                      </label>
                    ))}
                    {coach.procedures.some((p) => p.key === "follow_up") && (
                      <p className="text-[11px] text-stone-500" data-testid="quick-note-proc-follow-up">A follow-up is needed — the follow-up box above has been ticked for you.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-3">
            <button type="button" onClick={() => setHelpOpen((o) => !o)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${helpOpen ? "border-primary bg-primary text-white" : "border-stone-300 text-stone-500 hover:border-primary/40 hover:text-primary"}`}
              data-testid="quick-note-help-toggle">
              <Question size={13} /> Help me with this note
            </button>
            {helpOpen && (
              <div className="mt-2 rounded-lg border border-stone-200 bg-stone-50/60 p-3" data-testid="quick-note-help-thread">
                {helpLog.length === 0 && (
                  <p className="text-[11px] leading-relaxed text-stone-400">Ask anything — "what do I do with this?", "is this a complaint?" — the answers come from your agency documents. Push back if it read the note wrong; it's a conversation, and you're the one who decides.</p>
                )}
                <div className="space-y-2">
                  {helpLog.map((x, i) => (
                    <div key={i} className="space-y-1" data-testid={`quick-note-help-exchange-${i}`}>
                      <p className="ml-auto w-fit max-w-[85%] rounded-lg bg-primary px-3 py-1.5 text-xs text-white">{x.q}</p>
                      <div className="w-fit max-w-[90%] rounded-lg border border-stone-200 bg-white px-3 py-1.5">
                        <p className="text-xs leading-relaxed text-stone-700">{x.a}</p>
                        {(x.sources || []).length > 0 && <p className="mt-1 text-[10px] font-medium uppercase tracking-wide text-stone-400">Based on: {x.sources.join(", ")}</p>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <input value={helpQ} onChange={(e) => setHelpQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); askHelp(); } }}
                    placeholder='e.g. "family is upset about a late aide — what are we required to do?"'
                    className="h-9 flex-1 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="quick-note-help-input" />
                  <Button onClick={askHelp} disabled={helpBusy} variant="outline" className="rounded-full border-stone-300 text-xs" data-testid="quick-note-help-ask">
                    {helpBusy ? "Thinking…" : helpLog.length ? "Reply" : "Ask"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      </div>

      {complaintPrompt && (
        <div className="mt-3 rounded-lg border border-[#B7791F]/40 bg-[#B7791F]/[0.06] p-3" data-testid="complaint-prompt">
          <p className="text-sm font-semibold text-stone-900">Is this a complaint about the agency or a caregiver?</p>
          <p className="mt-0.5 text-xs text-stone-500 line-clamp-2">{complaintPrompt.text}</p>
          <p className="mt-1 text-[11px] text-stone-400">If it's about health, insurance, or family matters — it's not a loggable complaint.</p>
          <div className="mt-2 flex gap-2">
            <button onClick={async () => {
              try {
                await api.post(`/notes/quick/${complaintPrompt.id}/flag-complaint`, {});
                toast.success("Added to the complaints log — it's on the dashboard for review");
              } catch (e) { toast.error(formatApiError(e)); }
              setComplaintPrompt(null);
            }} className="rounded-full bg-[#B7791F] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#8a5d14]" data-testid="complaint-prompt-yes">
              Yes — log it as a complaint
            </button>
            <button onClick={async () => {
              try { await api.post(`/notes/quick/${complaintPrompt.id}/not-complaint`, {}); } catch (e) { /* best-effort learning */ }
              setComplaintPrompt(null);
            }} className="rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-600" data-testid="complaint-prompt-no">
              Not a complaint
            </button>
          </div>
        </div>
      )}

      {attentionNotes.length > 0 && showAttention && (
        <div className="mt-5 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-3" data-testid="quick-note-attention">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#8a5d14]">Open items — close a follow-up or match people</p>
          <div className="mt-2 space-y-1.5">
            {attentionNotes.slice(0, 6).map((n) => (
              <div key={n.id} className="rounded-lg border border-[#B7791F]/20 bg-white px-3 py-2" data-testid={`quick-note-row-${n.id}`}>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className={`rounded-full px-2 py-0.5 font-bold ${URGENCY_CLS[n.urgency] || URGENCY_CLS.routine}`}>{TYPE_LABELS[n.note_type] || n.note_type}</span>
                  <span className="w-full text-stone-700 sm:w-auto sm:flex-1">{n.cleaned}</span>
                  {[...(n.client_id ? [[n.client_id, n.client_name]] : []), ...(n.also_client_ids || []).map((id, i) => [id, (n.also_client_names || [])[i]])].map(([id, nm]) => (
                    <Link key={id} to={`/clients/${id}`} className="font-semibold text-primary hover:underline" data-testid={`quick-note-client-link-${n.id}-${id}`}>{nm}</Link>
                  ))}
                  {[...(n.employee_id ? [[n.employee_id, n.employee_name]] : []), ...(n.also_employee_ids || []).map((id, i) => [id, (n.also_employee_names || [])[i]])].map(([id, nm]) => (
                    <Link key={id} to={`/team/${id}`} className="font-semibold text-primary hover:underline" data-testid={`quick-note-employee-link-${n.id}-${id}`}>{nm}</Link>
                  ))}
                  {(n.follow_up_date || n.event_date) ? (
                    <Link to={`/calendar?focus=${n.follow_up_date || n.event_date}`} className="font-medium text-primary hover:underline" title="See on the calendar" data-testid={`quick-note-cal-link-${n.id}`}>{n.follow_up_date || n.event_date}</Link>
                  ) : (
                    <span className="text-stone-300">{(n.created_at || "").slice(0, 10)}</span>
                  )}
                  {(n.needs_follow_up || n.matched_at) && !n.done && (
                    <button onClick={() => markDone(n)} className="inline-flex items-center gap-1 font-semibold text-[#1e8449] hover:underline" data-testid={`quick-note-done-${n.id}`}>
                      <CheckCircle size={12} /> {n.needs_follow_up ? "done" : "matched ✓ — clear"}
                    </button>
                  )}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {(n.tags || []).map((t) => (
                    <button key={t} type="button" title="Remove tag" onClick={() => setTags(n, (n.tags || []).filter((x) => x !== t))}
                      className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-500 transition-colors hover:bg-[#C0392B]/10 hover:text-[#C0392B]"
                      data-testid={`quick-note-row-tag-${n.id}-${t}`}>#{t} ×</button>
                  ))}
                  <select value="" onChange={(e) => { if (e.target.value) setTags(n, [...(n.tags || []), e.target.value]); }}
                    className="h-6 rounded-md border border-stone-200 bg-white px-1 text-[10px] text-stone-500" data-testid={`quick-note-add-tag-${n.id}`}>
                    <option value="">+ tag</option>
                    {NOTE_TAGS.filter((t) => !(n.tags || []).includes(t)).map((t) => <option key={t} value={t}>#{t}</option>)}
                  </select>
                  {n.original && n.cleaned === n.original && (
                    <button type="button" onClick={() => reclean(n)} className="text-[10px] font-semibold text-primary hover:underline" data-testid={`quick-note-reclean-${n.id}`}>Fix spelling</button>
                  )}
                  {(!n.done) && (
                    <button type="button" onClick={() => { setRelinkFor(relinkFor === n.id ? null : n.id); setRelinkClients([""]); setRelinkEmps([""]); }}
                      className="text-[10px] font-semibold text-primary hover:underline" data-testid={`quick-note-relink-${n.id}`}>{n.client_id || n.employee_id ? "Match more people" : "Match people"}</button>
                  )}
                  <FileAsSelect n={n} onDone={loadNotes} />
                </div>
                {relinkFor === n.id && (
                  <div className="mt-1.5 space-y-1.5" data-testid={`quick-note-relink-form-${n.id}`}>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {relinkClients.map((v, i) => (
                        <select key={`c${i}`} value={v} onChange={(e) => setRelinkClients((s) => s.map((x, j) => (j === i ? e.target.value : x)))}
                          className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`quick-note-relink-client-${n.id}-${i}`}>
                          <option value="">— client —</option>
                          {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name || c.name}</option>)}
                        </select>
                      ))}
                      <button type="button" onClick={() => setRelinkClients((s) => [...s, ""])} className="text-[10px] font-semibold text-primary hover:underline" data-testid={`quick-note-relink-add-client-${n.id}`}>+ another client</button>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {relinkEmps.map((v, i) => (
                        <select key={`e${i}`} value={v} onChange={(e) => setRelinkEmps((s) => s.map((x, j) => (j === i ? e.target.value : x)))}
                          className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`quick-note-relink-employee-${n.id}-${i}`}>
                          <option value="">— employee —</option>
                          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      ))}
                      <button type="button" onClick={() => setRelinkEmps((s) => [...s, ""])} className="text-[10px] font-semibold text-primary hover:underline" data-testid={`quick-note-relink-add-employee-${n.id}`}>+ another employee</button>
                      <Button size="sm" onClick={() => relink(n.id)} className="h-8 rounded-full bg-primary text-[11px] text-white hover:bg-stone-900" data-testid={`quick-note-relink-save-${n.id}`}>Link &amp; file</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {recentNotes.length > 0 && showRecent && (
        <div className="mt-4 rounded-lg border border-stone-100 bg-stone-50/60 p-3" data-testid="quick-note-recent">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Last few notes — filed, nothing needed</p>
          <div className="mt-2 space-y-1">
            {recentNotes.slice(0, 12).map((n) => (
              <div key={n.id} className="flex flex-wrap items-center gap-2 px-1 py-1 text-xs text-stone-500" data-testid={`quick-note-recent-row-${n.id}`}>
                <span className="rounded-full bg-stone-200/70 px-2 py-0.5 text-[10px] font-bold text-stone-500">{TYPE_LABELS[n.note_type] || n.note_type}</span>
                <span className="w-full sm:w-auto sm:flex-1">{n.cleaned}</span>
                {(n.tags || []).map((t) => <span key={t} className="text-[10px] font-semibold text-stone-400">#{t}</span>)}
                {n.client_id && <Link to={`/clients/${n.client_id}`} className="font-semibold text-primary hover:underline" data-testid={`quick-note-recent-client-${n.id}`}>{n.client_name}</Link>}
                {n.employee_id && <Link to={`/team/${n.employee_id}`} className="font-semibold text-primary hover:underline" data-testid={`quick-note-recent-employee-${n.id}`}>{n.employee_name}</Link>}
                {(n.follow_up_date || n.event_date) ? (
                  <Link to={`/calendar?focus=${n.follow_up_date || n.event_date}`} className="text-primary/70 hover:underline" data-testid={`quick-note-recent-cal-${n.id}`}>{n.follow_up_date || n.event_date}</Link>
                ) : (
                  <span className="text-stone-300">{(n.created_at || "").slice(0, 10)}</span>
                )}
                <FileAsSelect n={n} onDone={loadNotes} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
